import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import Layout from "../Layout";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  X,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import navigationRoutes from "../../utils/navigationRoutes";
import * as XLSX from "xlsx";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { useQueryClient } from "@tanstack/react-query";

// Field name to display label mapping (from PP_CreateProperty)
const FIELD_LABELS: Record<string, string> = {
  caseno: "Case No",
  parcelid: "Parcel ID (APN)",
  date: "Date",
  enddate: "End Date (for Bid)",
  lienamount: "Lien Amount",
  startingbid: "Starting Bid",
  owners: "Owner",
  persqftprice: "Per Sqft Price",
  yearbuilt: "Year Built",
  bed: "Bed",
  bath: "Bath",
  sqfootage: "Sq. Footage",
  address: "Address",
  propertyname: "Property Name",
  finalarv: "Final ARV",
  rougharv: "Rough ARV",
  rehabestimate: "Rehab Estimate",
  notes: "Notes",
};

// Field mapping: CSV/Excel column names to form field names (for auto-mapping)
const FIELD_MAPPING: Record<string, string> = {
  "Case No": "caseno",
  "Case No.": "caseno",
  CaseNo: "caseno",
  "case no": "caseno",
  "Case Number": "caseno",
  "Parcel ID": "parcelid",
  "Parcel ID (APN etc)": "parcelid",
  ParcelID: "parcelid",
  "parcel id": "parcelid",
  APN: "parcelid",
  "Parcel ID (APN)": "parcelid",
  Date: "date",
  date: "date",
  "End Date": "enddate",
  "End Date (For Bid)": "enddate",
  EndDate: "enddate",
  "end date": "enddate",
  "Lien Amount": "lienamount",
  LienAmount: "lienamount",
  "lien amount": "lienamount",
  "Starting Bid": "startingbid",
  StartingBid: "startingbid",
  "starting bid": "startingbid",
  "Property name": "propertyname",
  "Property Name": "propertyname",
  PropertyName: "propertyname",
  "property name": "propertyname",
  Owners: "owners",
  owners: "owners",
  "Owner Name": "owners",
  Owner: "owners",
  "Per Sqft Price": "persqftprice",
  "Per SqftPrice": "persqftprice",
  PerSqftPrice: "persqftprice",
  "per sqft price": "persqftprice",
  "Year built": "yearbuilt",
  "Year Built": "yearbuilt",
  YearBuilt: "yearbuilt",
  "year built": "yearbuilt",
  Bed: "bed",
  bed: "bed",
  Bedrooms: "bed",
  Bath: "bath",
  bath: "bath",
  Bathrooms: "bath",
  "Sq. Footage": "sqfootage",
  "Sq Footage": "sqfootage",
  SqFootage: "sqfootage",
  "sq footage": "sqfootage",
  "Square Footage": "sqfootage",
  Address: "address",
  address: "address",
  "Rough ARV": "rougharv",
  RoughARV: "rougharv",
  "rough arv": "rougharv",
  "Final ARV": "finalarv",
  FinalARV: "finalarv",
  "final arv": "finalarv",
  "Rehab Estimate": "rehabestimate",
  RehabEstimate: "rehabestimate",
  "rehab estimate": "rehabestimate",
  Notes: "notes",
  notes: "notes",
};

// System fields available for mapping
const SYSTEM_FIELDS = [
  "caseno",
  "parcelid",
  "date",
  "enddate",
  "lienamount",
  "startingbid",
  "propertyname",
  "owners",
  "persqftprice",
  "yearbuilt",
  "bed",
  "bath",
  "sqfootage",
  "address",
  "rougharv",
  "finalarv",
  "rehabestimate",
  "notes",
];

interface ColumnMapping {
  uploadedColumn: string;
  systemField: string | null;
}

const PP_ImportProperty: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [fileData, setFileData] = useState<any[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const schemaName = "Property Master";

  // Auto-map columns based on FIELD_MAPPING
  const autoMapColumns = useCallback((columns: string[]): ColumnMapping[] => {
    const mappings: ColumnMapping[] = [];
    const usedSystemFields = new Set<string>();

    columns.forEach(column => {
      const normalizedColumn = column.trim();
      // Try exact match first
      let mappedField: string | undefined = FIELD_MAPPING[normalizedColumn];

      // Try case-insensitive match
      if (!mappedField) {
        const lowerColumn = normalizedColumn.toLowerCase();
        const found = Object.entries(FIELD_MAPPING).find(
          ([key]) => key.toLowerCase() === lowerColumn
        );
        mappedField = found ? found[1] : undefined;
      }

      // Try partial match (contains)
      if (!mappedField) {
        const lowerColumn = normalizedColumn.toLowerCase();
        const found = Object.entries(FIELD_MAPPING).find(
          ([key]) =>
            lowerColumn.includes(key.toLowerCase()) ||
            key.toLowerCase().includes(lowerColumn)
        );
        mappedField = found ? found[1] : undefined;
      }

      // If field already used, don't auto-map
      if (mappedField && usedSystemFields.has(mappedField)) {
        mappedField = undefined;
      }

      if (mappedField) {
        usedSystemFields.add(mappedField);
      }

      mappings.push({
        uploadedColumn: column,
        systemField: mappedField ?? null,
      });
    });

    return mappings;
  }, []);

  const parseFile = useCallback(async (file: File) => {
    try {
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      let columns: string[] = [];
      let dataRows: any[] = [];

      if (fileExtension === ".csv") {
        // Parse CSV
        const text = await file.text();
        const lines = text.split("\n").filter(line => line.trim());
        if (lines.length > 0) {
          // Handle CSV with potential quotes and commas
          const firstLine = lines[0];
          columns = firstLine
            .split(",")
            .map(col => col.trim().replace(/^"|"$/g, ""));

          // Parse data rows
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i]
              .split(",")
              .map(val => val.trim().replace(/^"|"$/g, ""));
            const row: any = {};
            columns.forEach((col, index) => {
              row[col] = values[index] ?? "";
            });
            dataRows.push(row);
          }
        }
      } else {
        // Parse Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Get all data
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        }) as any[][];

        if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
          // First row is headers
          columns = jsonData[0]
            .map(col =>
              col !== undefined && col !== null ? String(col).trim() : ""
            )
            .filter(col => col.length > 0);

          // Parse data rows
          for (let i = 1; i < jsonData.length; i++) {
            const row: any = {};
            columns.forEach((col, index) => {
              const value = jsonData[i][index];
              row[col] =
                value !== undefined && value !== null
                  ? String(value).trim()
                  : "";
            });
            // Only add row if it has at least one non-empty value
            if (Object.values(row).some(val => val !== "")) {
              dataRows.push(row);
            }
          }
        }
      }

      // Filter out empty columns
      columns = columns.filter(col => col.length > 0);

      // Filter out photo and county sites columns
      const excludedPatterns = [
        /^photos?$/i,
        /^photos?\s*\d+$/i,
        /^photo\s*\d+$/i,
        /^county\s*sites?$/i,
        /^county\s*sites?\s*\d+$/i,
      ];
      const excludedColumns = columns.filter(col => {
        const normalizedCol = col.trim();
        return excludedPatterns.some(pattern => pattern.test(normalizedCol));
      });

      // Filter out excluded columns from data rows
      dataRows = dataRows.map(row => {
        const filteredRow: any = {};
        Object.keys(row).forEach(key => {
          if (!excludedColumns.includes(key)) {
            filteredRow[key] = row[key];
          }
        });
        return filteredRow;
      });

      columns = columns.filter(col => {
        const normalizedCol = col.trim();
        return !excludedPatterns.some(pattern => pattern.test(normalizedCol));
      });

      return { columns, dataRows };
    } catch (error: any) {
      console.error("Parse error:", error);
      throw new Error(`Failed to parse file ${file.name}: ${error.message}`);
    }
  }, []);

  const parseMultipleFiles = useCallback(
    async (files: File[]) => {
      try {
        const allColumns = new Set<string>();
        const allDataRows: any[] = [];
        const parseResults: Array<{
          file: File;
          columns: string[];
          dataRows: any[];
        }> = [];

        // Parse all files
        for (const file of files) {
          const result = await parseFile(file);
          if (result && result.columns.length > 0) {
            result.columns.forEach(col => allColumns.add(col));
            allDataRows.push(...result.dataRows);
            parseResults.push({ file, ...result });
          }
        }

        if (allColumns.size === 0) {
          toast.error("No columns found in any of the files");
          return;
        }

        // Merge columns from all files (union)
        const mergedColumns = Array.from(allColumns);

        // Normalize all data rows to have the same columns
        const normalizedDataRows = allDataRows.map(row => {
          const normalizedRow: any = {};
          mergedColumns.forEach(col => {
            normalizedRow[col] = row[col] ?? "";
          });
          return normalizedRow;
        });

        const mappings = autoMapColumns(mergedColumns);
        setColumnMappings(mappings);
        setFileData(normalizedDataRows);
        setShowMapping(true);

        const totalRows = normalizedDataRows.length;
        const totalFiles = files.length;
        toast.success(
          `Found ${mergedColumns.length} columns and ${totalRows} rows across ${totalFiles} file${totalFiles > 1 ? "s" : ""}`
        );
      } catch (error: any) {
        console.error("Parse error:", error);
        toast.error(
          error.message ??
            "Failed to parse files. Please check the file format."
        );
      }
    },
    [parseFile, autoMapColumns]
  );

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      // Validate file types
      const validExtensions = [".xlsx", ".xls", ".csv"];
      const invalidFiles: File[] = [];

      files.forEach(file => {
        const fileExtension = file.name
          .substring(file.name.lastIndexOf("."))
          .toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
          invalidFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        toast.error(
          `Invalid file type(s): ${invalidFiles.map(f => f.name).join(", ")}. Please upload Excel (.xlsx, .xls) or CSV files.`
        );
        // Remove invalid files
        files = files.filter(
          file => !invalidFiles.some(invalid => invalid === file)
        );
      }

      if (files.length === 0) {
        return;
      }

      setSelectedFiles(files);
      await parseMultipleFiles(files);
    },
    [parseMultipleFiles]
  );

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) {
        await handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) {
        await handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleMappingChange = useCallback(
    (index: number, systemField: string | null) => {
      setColumnMappings(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          systemField,
        };
        return updated;
      });
    },
    []
  );

  // Validate mappings and data before showing preview
  const validateMappings = useCallback((): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    rowErrors: Array<{ row: number; errors: string[] }>;
  } => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const rowErrors: Array<{ row: number; errors: string[] }> = [];

    // 1. MAPPING VALIDATIONS
    // Check if at least one field is mapped
    const hasMappings = columnMappings.some(
      m => m.systemField !== null && m.systemField !== "none"
    );
    if (!hasMappings) {
      errors.push("Please map at least one column to a system field");
    }

    // Check if essential fields are mapped (at least one identifier)
    const essentialFields = ["caseno", "parcelid", "propertyname"];
    const mappedEssentialFields = columnMappings.filter(
      m => m.systemField && essentialFields.includes(m.systemField)
    );
    if (mappedEssentialFields.length === 0) {
      errors.push(
        "Please map at least one essential field (Case No, Parcel ID, or Property Name)"
      );
    }

    // 2. DATA EXISTENCE VALIDATIONS
    // Check if file data exists
    if (fileData.length === 0) {
      errors.push("No data rows found in the uploaded file(s)");
      return { isValid: false, errors, warnings, rowErrors };
    }

    // Validate that mapped columns have data
    const mappedColumns = columnMappings
      .filter(m => m.systemField && m.systemField !== "none")
      .map(m => m.uploadedColumn);

    if (mappedColumns.length > 0 && fileData.length > 0) {
      const sampleRow = fileData[0];
      const emptyMappedColumns = mappedColumns.filter(col => {
        const value = sampleRow[col];
        return value === undefined || value === null || value === "";
      });

      if (emptyMappedColumns.length === mappedColumns.length) {
        errors.push("All mapped columns appear to be empty in the data");
      }
    }

    // 3. ROW-LEVEL VALIDATIONS
    const mappedFields = columnMappings
      .filter(m => m.systemField && m.systemField !== "none")
      .map(m => ({
        systemField: m.systemField!,
        uploadedColumn: m.uploadedColumn,
      }));

    // Track duplicates
    const caseNoMap = new Map<string, number[]>();
    const parcelIdMap = new Map<string, number[]>();

    fileData.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because row 1 is header, and index is 0-based
      const rowIssues: string[] = [];
      const rowData: any = {};

      // Extract mapped values
      mappedFields.forEach(({ systemField, uploadedColumn }) => {
        const value = row[uploadedColumn];
        rowData[systemField] = value;
      });

      // Check for completely empty rows
      const hasAnyValue = Object.values(rowData).some(
        val => val !== undefined && val !== null && val !== ""
      );
      if (!hasAnyValue) {
        rowIssues.push("Row is completely empty");
      }

      // Text field validations
      if (
        rowData.caseno !== undefined &&
        rowData.caseno !== null &&
        rowData.caseno !== ""
      ) {
        const caseNo = String(rowData.caseno).trim();
        if (caseNo.length === 0) {
          rowIssues.push("Case No is empty");
        } else {
          // Track for duplicates
          if (!caseNoMap.has(caseNo)) {
            caseNoMap.set(caseNo, []);
          }
          caseNoMap.get(caseNo)!.push(rowNumber);
        }
      }

      if (
        rowData.parcelid !== undefined &&
        rowData.parcelid !== null &&
        rowData.parcelid !== ""
      ) {
        const parcelId = String(rowData.parcelid).trim();
        if (parcelId.length === 0) {
          rowIssues.push("Parcel ID is empty");
        } else {
          // Track for duplicates
          if (!parcelIdMap.has(parcelId)) {
            parcelIdMap.set(parcelId, []);
          }
          parcelIdMap.get(parcelId)!.push(rowNumber);
        }
      }

      if (rowIssues.length > 0) {
        rowErrors.push({ row: rowNumber, errors: rowIssues });
      }
    });

    // 4. DUPLICATE VALIDATIONS
    caseNoMap.forEach((rows, caseNo) => {
      if (rows.length > 1) {
        errors.push(
          `Duplicate Case No "${caseNo}" found in rows: ${rows.join(", ")}`
        );
      }
    });

    parcelIdMap.forEach((rows, parcelId) => {
      if (rows.length > 1) {
        errors.push(
          `Duplicate Parcel ID "${parcelId}" found in rows: ${rows.join(", ")}`
        );
      }
    });

    // 5. DATA QUALITY WARNINGS
    if (rowErrors.length > 0) {
      warnings.push(`${rowErrors.length} row(s) have validation issues`);
    }

    const emptyRows = rowErrors.filter(re =>
      re.errors.some(e => e.includes("completely empty"))
    );
    if (emptyRows.length > 0) {
      warnings.push(
        `${emptyRows.length} completely empty row(s) will be skipped`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      rowErrors,
    };
  }, [columnMappings, fileData]);

  const importProperties = useCallback(async () => {
    // Final validation before import
    const validation = validateMappings();
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    if (selectedFiles.length === 0 || fileData.length === 0) {
      toast.error("No file data to import");
      return;
    }

    setIsImporting(true);
    toast.loading("Importing properties...", { id: "import-properties" });

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      const importedIds: string[] = []; // Track imported property IDs

      // Process each row
      for (let i = 0; i < fileData.length; i++) {
        const row = fileData[i];
        const propertyData: any = {};

        // Map each column to system field
        columnMappings.forEach(mapping => {
          if (mapping.systemField && mapping.systemField !== "none") {
            const value = row[mapping.uploadedColumn];

            if (value !== undefined && value !== null && value !== "") {
              // Convert value based on field type
              let processedValue: any = value;

              // Handle numeric fields
              const numericFields = [
                "lienamount",
                "startingbid",
                "persqftprice",
                "yearbuilt",
                "bed",
                "bath",
                "sqfootage",
                "rougharv",
                "finalarv",
                "rehabestimate",
              ];
              if (numericFields.includes(mapping.systemField)) {
                const numValue = parseFloat(String(value));
                if (!isNaN(numValue)) {
                  processedValue = numValue;
                }
              }

              // Handle date fields
              if (
                mapping.systemField === "date" ||
                mapping.systemField === "enddate"
              ) {
                const dateValue = new Date(value);
                if (!isNaN(dateValue.getTime())) {
                  processedValue = dateValue.toISOString().split("T")[0];
                }
              }

              propertyData[mapping.systemField] = processedValue;
            }
          }
        });

        // Only create property if there's at least one field with value
        if (Object.keys(propertyData).length > 0) {
          try {
            const response = await DynamicModuleService.createItem(
              schemaName,
              propertyData
            );
            // Extract the created property ID from response
            const createdId =
              response?.id ??
              response?.Id ??
              response?.ID ??
              response?.data?.id ??
              null;
            if (createdId) {
              importedIds.push(String(createdId));
            }
            successCount++;
          } catch (error: any) {
            errorCount++;
            const errorMsg = `Row ${i + 2}: ${error?.response?.data?.message ?? error?.message ?? "Unknown error"}`;
            errors.push(errorMsg);
            console.error(`Error importing row ${i + 2}:`, error);
          }
        }
      }

      // Save imported property IDs to localStorage (frontend-only tracking)
      if (importedIds.length > 0) {
        try {
          const existingIds = localStorage.getItem("importedPropertyIds");
          const existingArray = existingIds ? JSON.parse(existingIds) : [];
          const updatedArray = [...new Set([...existingArray, ...importedIds])];
          localStorage.setItem(
            "importedPropertyIds",
            JSON.stringify(updatedArray)
          );
        } catch (error) {
          console.error(
            "Error saving imported property IDs to localStorage:",
            error
          );
        }
      }

      // Save import history to localStorage
      try {
        const importHistoryItem = {
          date: new Date().toISOString().split("T")[0],
          fileName:
            selectedFiles.length === 1
              ? selectedFiles[0].name
              : `${selectedFiles.length} files: ${selectedFiles.map(f => f.name).join(", ")}`,
          rows: fileData.length,
          success: successCount,
          errors: errorCount,
          status:
            errorCount > 0
              ? errorCount === fileData.length
                ? "Failed"
                : "Completed with Errors"
              : "Completed",
        };

        const existingHistory = localStorage.getItem("importHistory");
        const historyArray = existingHistory ? JSON.parse(existingHistory) : [];
        // Add new import to the beginning of the array (most recent first)
        historyArray.unshift(importHistoryItem);
        // Keep only last 50 imports
        const trimmedHistory = historyArray.slice(0, 50);
        localStorage.setItem("importHistory", JSON.stringify(trimmedHistory));
      } catch (error) {
        console.error("Error saving import history to localStorage:", error);
      }

      // Invalidate queries to refresh the property list
      await queryClient.invalidateQueries({
        queryKey: ["moduleData", schemaName],
      });

      toast.success(
        `Import completed! ${successCount} properties imported successfully${errorCount > 0 ? `, ${errorCount} failed` : ""}`,
        { id: "import-properties" }
      );

      if (errors.length > 0 && errors.length <= 5) {
        errors.forEach(error => toast.error(error));
      } else if (errors.length > 5) {
        toast.error(
          `${errors.length} errors occurred. Check console for details.`
        );
        console.error("Import errors:", errors);
      }

      // Navigate back to property master after successful import
      setTimeout(() => {
        navigate("/property-portal" + navigationRoutes.PROPERTY_MASTER);
      }, 1500);
    } catch (error: any) {
      console.error("Import error:", error);
      toast.error(
        error?.response?.data?.message ?? "Failed to import properties",
        { id: "import-properties" }
      );
    } finally {
      setIsImporting(false);
      setShowPreview(false);
    }
  }, [
    columnMappings,
    fileData,
    selectedFiles,
    schemaName,
    queryClient,
    navigate,
    validateMappings,
  ]);

  // Generate preview data based on mappings
  const getPreviewData = useCallback(() => {
    const previewRows = fileData.slice(0, 10); // Show first 10 rows
    const mappedFields = columnMappings.filter(
      m => m.systemField && m.systemField !== "none"
    );

    return previewRows.map((row, rowIndex) => {
      const previewRow: any = { _rowIndex: rowIndex + 1 };
      mappedFields.forEach(mapping => {
        const value = row[mapping.uploadedColumn];
        previewRow[mapping.systemField!] = value ?? "";
      });
      return previewRow;
    });
  }, [fileData, columnMappings]);

  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    rowErrors: Array<{ row: number; errors: string[] }>;
  } | null>(null);

  // Save rejected file to import history
  const saveRejectedFile = useCallback(() => {
    if (selectedFiles.length === 0 || fileData.length === 0) {
      return;
    }

    try {
      const importHistoryItem = {
        date: new Date().toISOString().split("T")[0],
        fileName:
          selectedFiles.length === 1
            ? selectedFiles[0].name
            : `${selectedFiles.length} files: ${selectedFiles.map(f => f.name).join(", ")}`,
        rows: fileData.length,
        success: 0,
        errors: fileData.length,
        status: "Rejected",
      };

      const existingHistory = localStorage.getItem("importHistory");
      const historyArray = existingHistory ? JSON.parse(existingHistory) : [];
      // Add new import to the beginning of the array (most recent first)
      historyArray.unshift(importHistoryItem);
      // Keep only last 50 imports
      const trimmedHistory = historyArray.slice(0, 50);
      localStorage.setItem("importHistory", JSON.stringify(trimmedHistory));
    } catch (error) {
      console.error("Error saving rejected file to import history:", error);
    }
  }, [selectedFiles, fileData]);

  const handleContinueToPreview = useCallback(() => {
    const validation = validateMappings();
    setValidationResult(validation);

    if (!validation.isValid) {
      validation.errors.forEach((error: string) => toast.error(error));
      if (validation.warnings.length > 0) {
        validation.warnings
          .slice(0, 3)
          .forEach((warning: string) => toast.warning(warning));
      }
      // Save rejected file to import history
      saveRejectedFile();
      return;
    }

    if (validation.warnings.length > 0) {
      validation.warnings
        .slice(0, 3)
        .forEach((warning: string) => toast.warning(warning));
    }

    setShowPreview(true);
  }, [validateMappings, saveRejectedFile]);

  const handleBackToMapping = useCallback(() => {
    setShowPreview(false);
  }, []);

  const handleBack = useCallback(() => {
    if (showPreview) {
      setShowPreview(false);
    } else if (showMapping) {
      // If user goes back from mapping without importing, check if validation failed
      const validation = validateMappings();
      if (
        !validation.isValid &&
        selectedFiles.length > 0 &&
        fileData.length > 0
      ) {
        // Save rejected file to import history
        saveRejectedFile();
      }
      setShowMapping(false);
      setColumnMappings([]);
      setFileData([]);
      setSelectedFiles([]);
    } else {
      navigate("/property-portal" + navigationRoutes.PROPERTY_MASTER);
    }
  }, [
    showMapping,
    showPreview,
    navigate,
    validateMappings,
    selectedFiles,
    fileData,
    saveRejectedFile,
  ]);

  const handleRemoveFile = useCallback(
    (index: number) => {
      setSelectedFiles(prev => {
        const updated = prev.filter((_, i) => i !== index);
        if (updated.length === 0) {
          setShowMapping(false);
          setColumnMappings([]);
          setFileData([]);
        } else {
          // Re-parse remaining files
          parseMultipleFiles(updated);
        }
        return updated;
      });
    },
    [parseMultipleFiles]
  );

  const getSystemFieldLabel = (fieldKey: string): string => {
    return FIELD_LABELS[fieldKey] || fieldKey;
  };

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center lg:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Upload/Import Property Data
          </h1>
        </div>

        {!showMapping && !showPreview && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Excel or CSV File</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors ${
                  isDragging
                    ? "border-[#4a5273] bg-[#4a5273]/5"
                    : "border-muted-foreground/25 bg-muted/10"
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="rounded-full bg-[#4a5273]/10 p-6">
                    <FileSpreadsheet className="h-12 w-12 text-[#4a5273]" />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-foreground">
                      Upload your property data file(s)
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Supports Excel (.xlsx, .xls) and CSV files. You can upload
                      multiple files at once.
                    </p>
                  </div>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".xlsx,.xls,.csv"
                      multiple
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() =>
                        document.getElementById("file-upload")?.click()
                      }
                      className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Choose File(s)
                    </Button>
                  </div>
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 w-full space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        Selected file{selectedFiles.length > 1 ? "s" : ""} (
                        {selectedFiles.length}):
                      </p>
                      <div className="max-h-60 space-y-2 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            className="flex items-center justify-between rounded-lg border bg-background p-3"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-foreground">
                                {file.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(file.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ml-2 h-8 w-8 flex-shrink-0"
                              onClick={() => handleRemoveFile(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline" onClick={handleBack}>
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {showMapping && !showPreview && (
          <Card>
            <CardHeader>
              <CardTitle>Map Columns to System Fields</CardTitle>
              <p className="mt-2 text-sm text-muted-foreground">
                Map your uploaded columns to the corresponding system fields.
                Auto-mapping has been applied where possible.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {columnMappings.map((mapping, index) => (
                  <div
                    key={mapping.uploadedColumn}
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                  >
                    <div className="space-y-2">
                      <Label>Uploaded Column:</Label>
                      <div className="rounded-md border bg-muted/50 p-3 text-sm font-medium">
                        {mapping.uploadedColumn}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>System Field:</Label>
                      <Select
                        value={mapping.systemField ?? "none"}
                        onValueChange={value =>
                          handleMappingChange(
                            index,
                            value === "none" ? null : value
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select system field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            -- Do not import --
                          </SelectItem>
                          {SYSTEM_FIELDS.map(fieldKey => (
                            <SelectItem key={fieldKey} value={fieldKey}>
                              {getSystemFieldLabel(fieldKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleContinueToPreview}
                  className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
                  disabled={isImporting}
                >
                  Continue to Preview
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {showPreview && (
          <Card>
            <CardHeader>
              <CardTitle>Data Preview</CardTitle>
              <p className="mt-2 text-sm text-muted-foreground">
                Review your data before importing. Showing{" "}
                {Math.min(10, fileData.length)} of {fileData.length} rows.
              </p>
            </CardHeader>
            <CardContent>
              {/* Validation Status */}
              {validationResult && (
                <div className="mb-6 space-y-3">
                  {validationResult.isValid ? (
                    <div className="rounded-lg border bg-green-50 p-4 dark:bg-green-950/20">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <p className="font-medium text-green-800 dark:text-green-300">
                          All validations passed. Ready to import!
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border bg-red-50 p-4 dark:bg-red-950/20">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />
                        <div className="flex-1">
                          <p className="mb-2 font-medium text-red-800 dark:text-red-300">
                            Validation errors found. Please fix before
                            importing.
                          </p>
                          <ul className="list-inside list-disc space-y-1 text-sm text-red-700 dark:text-red-400">
                            {validationResult.errors.slice(0, 5).map(error => (
                              <li key={`error-${error}`}>{error}</li>
                            ))}
                            {validationResult.errors.length > 5 && (
                              <li className="text-muted-foreground">
                                ...and {validationResult.errors.length - 5} more
                                error(s)
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {validationResult.warnings.length > 0 && (
                    <div className="rounded-lg border bg-yellow-50 p-4 dark:bg-yellow-950/20">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        <div className="flex-1">
                          <p className="mb-2 font-medium text-yellow-800 dark:text-yellow-300">
                            Warnings ({validationResult.warnings.length})
                          </p>
                          <ul className="max-h-32 list-inside list-disc space-y-1 overflow-y-auto text-sm text-yellow-700 dark:text-yellow-400">
                            {validationResult.warnings.map(warning => (
                              <li key={`warning-${warning}`}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Row Errors Summary */}
                  {validationResult.rowErrors.length > 0 && (
                    <div className="rounded-lg border bg-orange-50 p-4 dark:bg-orange-950/20">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-orange-600 dark:text-orange-400" />
                        <div className="flex-1">
                          <p className="mb-2 font-medium text-orange-800 dark:text-orange-300">
                            Row-level issues (
                            {validationResult.rowErrors.length} row(s))
                          </p>
                          <div className="max-h-40 overflow-y-auto text-sm text-orange-700 dark:text-orange-400">
                            {validationResult.rowErrors
                              .slice(0, 10)
                              .map(rowError => (
                                <div
                                  key={`row-error-${rowError.row}-${rowError.errors.join("-")}`}
                                  className="mb-1"
                                >
                                  <span className="font-semibold">
                                    Row {rowError.row}:
                                  </span>{" "}
                                  {rowError.errors.join("; ")}
                                </div>
                              ))}
                            {validationResult.rowErrors.length > 10 && (
                              <p className="mt-1 text-muted-foreground">
                                ...and {validationResult.rowErrors.length - 10}{" "}
                                more row(s) with issues
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Preview Table */}
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-12">#</TableHead>
                      {columnMappings
                        .filter(m => m.systemField && m.systemField !== "none")
                        .map(mapping => (
                          <TableHead key={mapping.systemField}>
                            <div className="flex flex-col">
                              <span className="font-semibold">
                                {getSystemFieldLabel(mapping.systemField!)}
                              </span>
                              <span className="text-xs font-normal text-muted-foreground">
                                ← {mapping.uploadedColumn}
                              </span>
                            </div>
                          </TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getPreviewData().map((row, index) => (
                      <TableRow key={`preview-row-${row._rowIndex ?? index}`}>
                        <TableCell className="font-medium">
                          {row._rowIndex}
                        </TableCell>
                        {columnMappings
                          .filter(
                            m => m.systemField && m.systemField !== "none"
                          )
                          .map(mapping => {
                            const value = row[mapping.systemField!];
                            return (
                              <TableCell key={mapping.systemField}>
                                {value !== null &&
                                value !== undefined &&
                                value !== "" ? (
                                  String(value)
                                ) : (
                                  <span className="text-muted-foreground">
                                    —
                                  </span>
                                )}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary Info */}
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                  <p className="text-2xl font-bold">{fileData.length}</p>
                </div>
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">Mapped Fields</p>
                  <p className="text-2xl font-bold">
                    {
                      columnMappings.filter(
                        m => m.systemField && m.systemField !== "none"
                      ).length
                    }
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">Files</p>
                  <p className="text-2xl font-bold">{selectedFiles.length}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleBackToMapping}
                  disabled={isImporting}
                >
                  Back to Mapping
                </Button>
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={importProperties}
                  className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
                  disabled={
                    isImporting ||
                    (validationResult ? !validationResult.isValid : false)
                  }
                >
                  {isImporting ? (
                    "Importing..."
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Import to Property Master
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PP_ImportProperty;
